import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { mergeRefs } from '../../../utils/merge-refs';
import { Field, FieldLabel, FieldProps } from '../field/field';
import { Validation } from '../text-input/text';

export interface TextAreaProps extends FieldProps<string> {
	onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
	placeholder?: string;
	defaultValue?: string;
	style?: { [key: string]: any };
	disabled?: boolean;
	id?: string;
	limit?: number;
	rows?: number;
	textAreaStyle?: { [key: string]: any };
	shouldUpdateValue?: boolean;
	value?: string;
	persistent?: boolean;
}

// eslint-disable-next-line react/display-name
const Textarea: React.FC<TextAreaProps> = React.forwardRef<
	HTMLTextAreaElement,
	TextAreaProps
>(
	(props, ref): JSX.Element => {
		const {
			tooltip,
			name,
			label,
			onChange,
			placeholder,
			defaultValue,
			style,
			disabled = false,
			id = '',
			valid = Validation.normal,
			onBlur = () => ({}),
			limit,
			rows = 4,
			textAreaStyle = {},
			shouldUpdateValue = false,
			value,
			required,
		} = props;
		const [entered, setEntered] = useState(false);
		const input = useRef<HTMLTextAreaElement | null>(null);
		const [len, setLen] = useState(0);

		function keyUp() {
			setEntered(!!input.current?.value);
			setLen(input.current?.value.length ?? 0);
		}

		useEffect(() => {
			if (defaultValue) {
				setEntered(true);
			}
		}, [defaultValue]);

		// since this component isn't a "controlled component" in the regular useState way
		// we need a way to update the value from a parent component. Passing in shouldUpdateValue={true}
		// will allow the input component value to update whenever defaultValue changes
		useEffect(() => {
			if (shouldUpdateValue && input?.current) {
				input.current.value = defaultValue || '';
			}
		}, [shouldUpdateValue, defaultValue]);

		return (
			<Field {...props}>
				<div
					className={classNames('field-group textarea', { entered }, valid)}
					style={style}
				>
					<FieldLabel label={label} required={required} tooltip={tooltip} />
					{/* Todo move to FieldLabel */}
					{/* <div className={tooltip ? 'tooltip' : ''}>
						{tooltip && (
							<img src={image} alt="tooltip" height="18px" width="auto" />
						)}{' '}
						<span className="tooltip-text">{tooltip && text}</span>
					 </div> */}
					<textarea
						name={name}
						onBlur={onBlur}
						id={id}
						disabled={disabled}
						ref={mergeRefs(input, ref)}
						placeholder={placeholder || label}
						onChange={onChange}
						onKeyUp={keyUp}
						defaultValue={defaultValue ?? ''}
						value={value}
						maxLength={limit ? limit : 64000}
						rows={rows}
						style={textAreaStyle}
					></textarea>
					{limit && (
						<div className="limit-indicator">
							<span>
								{len}/{limit}
							</span>
						</div>
					)}
				</div>
			</Field>
		);
	}
);
export default Textarea;
